import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import { useTask } from "../task/hooks/useTasks";
import { usePlannerOverviews } from "../planner/hooks/usePlanner";
import PlannerCalendarView from "../planner/components/PlannerCalendarView";
import {
    buildDailyPlanFromPlannerOverview,
    buildWeekBreakdownFromPlannerOverview,
} from "../planner/utils/plannerOverviewTransform";

export default function HomeDailyPlannerWidget() {
    const navigate = useNavigate();
    const { authToken, isLoading: authLoading } = useAuth();
    const { data: tasksData, isLoading: isTasksLoading } = useTask(authToken);
    const { data: plannerOverviewData, isLoading: isPlannerLoading } = usePlannerOverviews(authToken);

    const tasksList = React.useMemo(() => (
        Array.isArray(tasksData) ? tasksData : (tasksData?.data || [])
    ), [tasksData]);

    const supportingWeekBreakdown = React.useMemo(
        () => buildWeekBreakdownFromPlannerOverview(plannerOverviewData?.weekly?.today || null),
        [plannerOverviewData],
    );
    const dailyPlan = React.useMemo(
        () => buildDailyPlanFromPlannerOverview(plannerOverviewData?.today?.today || null),
        [plannerOverviewData],
    );

    const isLoading = authLoading || isTasksLoading || isPlannerLoading;

    return (
        <div className="rounded-xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-lg">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold text-gray-200">Today&apos;s Plan</h2>
                    <p className="mt-1 text-sm text-gray-400">A quick view of today&apos;s scheduled plan and task deadlines.</p>
                </div>
                <button
                    type="button"
                    onClick={() => navigate("/planner")}
                    className="text-xs text-gray-400 transition-colors hover:text-gray-200"
                >
                    Open planner →
                </button>
            </div>

            {isLoading ? (
                <div className="rounded-xl border border-[#30384D] bg-[#23293A] px-4 py-8 text-center text-sm text-gray-400">
                    Loading today&apos;s plan...
                </div>
            ) : (
                <PlannerCalendarView
                    effectivePeriod="today"
                    planSectionsData={{ mainOutcome: dailyPlan.mainOutcome }}
                    planFocusItems={dailyPlan.focusItems}
                    supportingWeekBreakdown={supportingWeekBreakdown}
                    supportingWeekPeriodKey="weekly"
                    tasksList={tasksList}
                    now={new Date()}
                    planDate={new Date()}
                    isReadOnly
                    showInfoBar={false}
                    showToolbar={false}
                    calendarHeight={360}
                    wrapperClassName="space-y-3"
                    calendarContainerClassName="px-2 py-2"
                    emptyStateMessage="No scheduled planner items or task deadlines for today yet."
                />
            )}
        </div>
    );
}
